/* Brave Bottoms "Mystery Discount" popup (sections/bb-mystery.liquid).
   - Opens after data-delay seconds on the page (optionally also on exit intent on computers).
   - Step 1 answer -> customer tag, step 2 email -> Shopify customer form, step 3 code -> applied to checkout.
   - "No thanks" keeps it closed for data-snooze days; a corner button reopens it.
   - data-teaser="bar": a floating "Mystery Discount" pill shows after data-bar-after px of scrolling and opens it
     anytime; its x hides the pill for the rest of the visit (sessionStorage).
   - In the theme editor it opens when the section is selected and never on its own. */
(() => {
  if (customElements.get('bb-mystery')) return;

  const KEY = 'bb-mystery:v1';
  const DAY = 864e5;
  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return {}; }
  };
  const write = (state) => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* storage blocked */ }
  };
  const sessionFlag = (name, value) => {
    try {
      if (value === undefined) return sessionStorage.getItem(name) === '1';
      sessionStorage.setItem(name, '1');
    } catch (e) { return false; }
    return true;
  };

  class BBMystery extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.bbm__dialog');
      this.form = this.querySelector('form');
      this.teaser = this.querySelector('[data-bbm-teaser]');
      if (!this.dialog || !this.form) return;
      this.design = this.hasAttribute('data-design');
      this.bar = this.dataset.teaser === 'bar' && this.teaser && this.teaser.hasAttribute('data-bbm-bar');
      this.state = this.design ? {} : read();
      this.form.noValidate = true;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCancel = (event) => { event.preventDefault(); this.close('dismiss'); };
      this.addEventListener('click', this.onClick);
      this.form.addEventListener('submit', this.onSubmit);
      this.dialog.addEventListener('cancel', this.onCancel);

      if (this.design) return this.setupEditor();

      // Came back from a plain (non-JS) form post
      if (this.querySelector('[data-bbm-posted]')) {
        this.claim();
        this.open(3);
        return;
      }
      if (this.querySelector('[data-bbm-failed]')) {
        this.open(2);
        return;
      }

      // Bottom bar: always available once the shopper scrolls, even before the popup has opened
      if (this.bar) this.showTeaser();
      if (this.state.claimed) return this.showTeaser();
      const snoozed = (this.state.snoozeUntil && Date.now() < this.state.snoozeUntil) || sessionFlag('bb-mystery:closed');
      if (snoozed) return this.showTeaser();
      this.arm();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      if (this.onLift) {
        window.removeEventListener('scroll', this.onLift, true);
        window.removeEventListener('resize', this.onLift);
        document.removeEventListener('transitionend', this.onLift, true);
      }
      if (this.editorHandlers) this.editorHandlers.forEach(([type, fn]) => document.removeEventListener(type, fn));
      document.documentElement.classList.remove('bbm-lock');
    }

    /* ---------- timing ---------- */
    arm() {
      const delay = Math.max(0, parseFloat(this.dataset.delay) || 0) * 1000;
      const wait = Math.max(0, delay - performance.now());
      this.timer = setTimeout(() => this.autoOpen(), wait);
      if (this.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.onLeave = (event) => {
          if (!event.relatedTarget && event.clientY <= 0 && performance.now() > 3000) this.autoOpen();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    busy() {
      // Wait while the cart drawer, search, quick add or another popup is open
      if (document.querySelector('dialog[open]:not(.bbm__dialog)')) return true;
      return Array.from(document.querySelectorAll('[aria-modal="true"]')).some((el) => {
        if (this.contains(el)) return false;
        const style = getComputedStyle(el);
        return el.getClientRects().length > 0 && style.visibility !== 'hidden' && style.display !== 'none';
      });
    }

    autoOpen() {
      if (this.opened || this.state.claimed) return;
      if (this.busy()) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.autoOpen(), 2000);
        return;
      }
      this.open(1);
    }

    /* ---------- open, close, steps ---------- */
    open(step) {
      this.opened = true;
      clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      this.go(step || 1);
      this.hideTeaser();
      if (!this.dialog.open) {
        try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
        // Start focus on the popup itself so no button shows a focus ring on its own
        if ((step || 1) === 1) this.dialog.focus({ preventScroll: true });
      }
      document.documentElement.classList.add('bbm-lock');
      this.dispatchEvent(new CustomEvent('bb-mystery:open', { bubbles: true, detail: { step } }));
    }

    close(reason) {
      if (this.dialog.open) this.dialog.close();
      this.dialog.removeAttribute('open');
      document.documentElement.classList.remove('bbm-lock');
      if (!this.design && !this.state.claimed && reason === 'dismiss') {
        const days = parseInt(this.dataset.snooze, 10) || 0;
        if (days > 0) {
          this.state.snoozeUntil = Date.now() + days * DAY;
          write(this.state);
        } else {
          sessionFlag('bb-mystery:closed', true);
        }
      }
      this.showTeaser();
    }

    go(step) {
      const n = String(step);
      this.dialog.dataset.step = n;
      this.querySelectorAll('[data-bbm-step]').forEach((panel) => {
        panel.hidden = panel.dataset.bbmStep !== n;
      });
      const title = this.querySelector(`[data-bbm-step="${n}"] .bbm__title`);
      if (title && title.id) this.dialog.setAttribute('aria-labelledby', title.id);
      const focusTarget = {
        1: '.bbm__option',
        2: 'input[type="email"]',
        3: '[data-bbm-copy], [data-bbm-done]',
      }[n];
      const el = n !== '1' && focusTarget && this.querySelector(`[data-bbm-step="${n}"] ${focusTarget}`);
      if (el && !this.design && this.dialog.open) requestAnimationFrame(() => el.focus({ preventScroll: true }));
    }

    /* ---------- clicks ---------- */
    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-bbm-close')) {
        event.preventDefault();
        this.close('dismiss');
      } else if (target.hasAttribute('data-bbm-option')) {
        event.preventDefault();
        const answer = target.dataset.bbmOption;
        this.state.challenge = answer;
        if (!this.design) write(this.state);
        const tags = this.querySelector('[data-bbm-tags]');
        if (tags) tags.value = `mystery-popup, challenge: ${answer.replace(/,/g, ' ')}`;
        this.go(2);
      } else if (target.hasAttribute('data-bbm-copy')) {
        event.preventDefault();
        this.copy(target);
      } else if (target.hasAttribute('data-bbm-done')) {
        event.preventDefault();
        this.close('done');
      } else if (target.hasAttribute('data-bbm-teaser') || target.hasAttribute('data-bbm-open')) {
        event.preventDefault();
        this.open(this.state.claimed ? 3 : this.state.challenge ? 2 : 1);
      } else if (target.hasAttribute('data-bbm-dismiss')) {
        event.preventDefault();
        sessionFlag('bb-mystery:pill-off', true);
        this.hideTeaser();
      }
    }

    copy(button) {
      const code = this.dataset.code || '';
      const done = () => {
        const original = button.dataset.label || button.textContent;
        button.dataset.label = original;
        button.textContent = button.dataset.done || 'Copied';
        setTimeout(() => { button.textContent = original; }, 1800);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, () => this.selectCode());
      } else {
        this.selectCode();
      }
    }

    selectCode() {
      const node = this.querySelector('[data-bbm-code]');
      if (!node) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    /* ---------- email ---------- */
    showError(show) {
      const error = this.querySelector('[data-bbm-error]');
      const input = this.form.querySelector('input[type="email"]');
      if (error) error.hidden = !show;
      if (input) input.setAttribute('aria-invalid', show ? 'true' : 'false');
      if (show && input) input.focus();
    }

    async onSubmit(event) {
      event.preventDefault();
      const input = this.form.querySelector('input[type="email"]');
      const email = (input && input.value.trim()) || '';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        this.showError(true);
        return;
      }
      input.value = email;
      this.showError(false);
      if (this.design) { this.go(3); return; }

      const button = this.querySelector('[data-bbm-submit]');
      if (button) button.setAttribute('aria-busy', 'true');
      this.state.pending = Date.now();
      write(this.state);
      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new URLSearchParams(new FormData(this.form)),
          credentials: 'same-origin',
        });
        // Shopify's bot check: let the shopper finish it on the real page, the popup reopens on return
        if (/\/challenge/.test(response.url)) return this.nativeSubmit();
        if (!response.ok) throw new Error(`status ${response.status}`);
        const html = await response.text();
        if (!/customer_posted=true/.test(response.url) && html.indexOf('data-bbm-failed') !== -1) {
          if (button) button.removeAttribute('aria-busy');
          this.showError(true);
          return;
        }
        if (button) button.removeAttribute('aria-busy');
        this.claim();
        this.go(3);
      } catch (error) {
        this.nativeSubmit();
      }
    }

    nativeSubmit() {
      HTMLFormElement.prototype.submit.call(this.form);
    }

    claim() {
      this.state.claimed = true;
      this.state.claimedAt = Date.now();
      delete this.state.pending;
      delete this.state.snoozeUntil;
      write(this.state);
      const code = this.dataset.code;
      if (this.dataset.apply === 'true' && code) {
        fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, {
          credentials: 'same-origin',
        }).catch(() => {});
      }
      if (this.bar) this.setBarText();
      this.dispatchEvent(new CustomEvent('bb-mystery:claimed', { bubbles: true, detail: { code } }));
    }

    /* ---------- corner button ---------- */
    showTeaser() {
      if (!this.teaser || this.design) return;
      if (this.bar && sessionFlag('bb-mystery:pill-off')) return;
      this.teaser.hidden = false;
      if (this.bar) this.setBarText();
      if (!this.onLift) {
        // Sit above sticky add-to-cart bars on phones
        let ticking = false;
        this.onLift = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.lift();
          });
        };
        window.addEventListener('scroll', this.onLift, { passive: true, capture: true });
        window.addEventListener('resize', this.onLift, { passive: true });
        // Sticky bars slide in with a transition after scrolling stops
        document.addEventListener('transitionend', this.onLift, true);
      }
      this.lift();
    }

    hideTeaser() {
      if (this.teaser) this.teaser.hidden = true;
      document.documentElement.classList.remove('bbm-has-bar');
    }

    setBarText() {
      if (!this.state.claimed) return;
      this.teaser.querySelectorAll('[data-claimed]').forEach((el) => {
        if (el.dataset.claimed) el.textContent = el.dataset.claimed;
      });
    }

    lift() {
      if (!this.teaser || this.teaser.hidden) return;
      if (this.bar) {
        const after = parseInt(this.dataset.barAfter, 10) || 0;
        const scrolled = window.scrollY || document.documentElement.scrollTop || 0;
        const show = scrolled >= after;
        this.teaser.classList.toggle('is-in', show);
        this.teaser.querySelectorAll('button').forEach((b) => { b.tabIndex = show ? 0 : -1; });
        this.teaser.setAttribute('aria-hidden', show ? 'false' : 'true');
        return;
      }
      const vh = window.innerHeight;
      let lift = 0;
      document.querySelectorAll('[class*="sticky"], sticky-add-to-cart').forEach((el) => {
        if (el === this.teaser || this.contains(el)) return;
        const style = getComputedStyle(el);
        if (style.position !== 'fixed' || style.visibility === 'hidden' || style.display === 'none' || +style.opacity === 0) return;
        const rect = el.getBoundingClientRect();
        if (rect.height < 20 || rect.width < 200) return;
        if (rect.bottom >= vh - 4 && rect.top > vh * 0.5 && rect.top < vh) lift = Math.max(lift, vh - rect.top);
      });
      this.teaser.style.setProperty('--bbm-lift', `${Math.round(lift)}px`);
    }

    /* ---------- theme editor ---------- */
    setupEditor() {
      const id = this.dataset.section;
      const step = parseInt(this.dataset.editorStep, 10) || 1;
      const on = (type, fn) => {
        document.addEventListener(type, fn);
        (this.editorHandlers = this.editorHandlers || []).push([type, fn]);
      };
      on('shopify:section:select', (event) => {
        if (event.detail && event.detail.sectionId === id) {
          window.__bbmSelected = id;
          this.open(step);
        }
      });
      on('shopify:block:select', (event) => {
        if (event.detail && event.detail.sectionId === id) {
          window.__bbmSelected = id;
          this.open(1);
        }
      });
      on('shopify:section:deselect', (event) => {
        if (event.detail && event.detail.sectionId === id) {
          window.__bbmSelected = null;
          this.close('editor');
          this.hideTeaser();
        }
      });
      // A settings change re-renders the section: stay open if it was selected
      if (window.__bbmSelected === id) this.open(step);
    }
  }

  customElements.define('bb-mystery', BBMystery);
})();